import React, { useState } from 'react';
import { X, Plus, Minus, Gift, Package, Check } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const AddonModal = ({ 
  isOpen, 
  onClose, 
  selectedProduct = null,
  onAddWithAddons 
}) => {
  const { addons: cartAddons, addAddon, removeAddon, updateAddonQuantity } = useCart();
  const [localAddons, setLocalAddons] = useState([...cartAddons]);
  
  const addonsData = [
    {
      id: 201,
      name: "Personalized Greeting Card",
      price: 49,
      description: "Custom handwritten message on a beautiful greeting card",
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=200&h=200&fit=crop"
    },
    {
      id: 202,
      name: "Birthday Balloon Set",
      price: 199,
      description: "Set of 5 decorative balloons with happy birthday message",
      image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=200&h=200&fit=crop"
    },
    {
      id: 203,
      name: "Soft Teddy Bear",
      price: 399,
      description: "Adorable 12-inch soft teddy bear for extra cuddles",
      image: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=200&h=200&fit=crop"
    },
    {
      id: 204,
      name: "Premium Chocolate Box",
      price: 299,
      description: "Assorted premium chocolates in elegant packaging",
      image: "https://images.unsplash.com/photo-1511381939415-e44015466834?w=200&h=200&fit=crop"
    }
  ];

  const getAddonQuantity = (addonId) => {
    const addon = localAddons.find(a => a.id === addonId);
    return addon ? addon.quantity : 0;
  };

  const handleAddAddon = (addon) => {
    const existing = localAddons.find(a => a.id === addon.id);
    if (existing) {
      setLocalAddons(prev => 
        prev.map(a => 
          a.id === addon.id ? { ...a, quantity: a.quantity + 1 } : a
        )
      );
    } else {
      setLocalAddons(prev => [...prev, { ...addon, quantity: 1 }]);
    }
  };

  const handleRemoveAddon = (addonId) => {
    setLocalAddons(prev => prev.filter(a => a.id !== addonId));
  };

  const handleUpdateQuantity = (addonId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveAddon(addonId);
      return;
    }
    setLocalAddons(prev => 
      prev.map(a => a.id === addonId ? { ...a, quantity: newQuantity } : a)
    );
  };

  const calculateAddonsTotal = () => {
    return localAddons.reduce((total, addon) => {
      return total + (addon.price * addon.quantity);
    }, 0);
  };

  const handleSaveAddons = () => {
    // Update cart with local addons
    localAddons.forEach(addon => {
      const existing = cartAddons.find(a => a.id === addon.id);
      if (!existing) {
        addAddon(addon);
      } else if (existing.quantity !== addon.quantity) {
        updateAddonQuantity(addon.id, addon.quantity);
      }
    });
    
    // Remove addons that were deselected
    cartAddons.forEach(cartAddon => {
      if (!localAddons.find(a => a.id === cartAddon.id)) {
        removeAddon(cartAddon.id);
      }
    });

    if (onAddWithAddons) {
      onAddWithAddons(localAddons.map(a => a.id));
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b p-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-pink-100 rounded-lg">
                <Gift className="h-4 w-4 text-pink-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Enhance Your Gift</h3>
                <p className="text-gray-600 text-xs">Make it extra special!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Selected Product */}
          {selectedProduct && (
            <div className="mb-4 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
              <div className="flex items-center gap-3">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-14 h-14 object-cover rounded-lg border-2 border-white shadow-sm"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                    {selectedProduct.name}
                  </h4>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-pink-600 font-bold text-sm">
                      ₹{selectedProduct.finalPrice || selectedProduct.price}
                    </p>
                    <span className="text-gray-500 text-xs">
                      Qty: {selectedProduct.quantity || 1}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleSaveAddons}
              className="w-full flex items-center justify-center bg-pink-600 text-white py-3 rounded-xl font-semibold hover:bg-pink-700 transition-colors text-sm"
            >
              {localAddons.length > 0 ? (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Save {localAddons.reduce((sum, a) => sum + a.quantity, 0)} Add-on(s)
                </>
              ) : (
                'Continue Without Add-ons'
              )}
            </button>
            
            <button
              onClick={onClose}
              className="w-full border border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              Skip Add-ons
            </button>
          </div>




          {/* Addons Grid - 2 per row */}
          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-3 text-sm">Select Add-ons</h4>
            <div className="grid grid-cols-2 gap-3">
              {addonsData.map((addon) => {
                const quantity = getAddonQuantity(addon.id);
                const isSelected = quantity > 0;
                
                return (
                  <div
                    key={addon.id}
                    className={`relative p-3 rounded-xl border-2 ${
                      isSelected 
                        ? 'border-pink-500 bg-pink-50' 
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    {/* Selection Badge */}
                    {isSelected && (
                      <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}

                    {/* Image */}
                    <div className="relative h-20 mb-2 overflow-hidden rounded-lg">
                      <img
                        src={addon.image}
                        alt={addon.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div>
                      <h5 className="font-semibold text-gray-900 text-xs mb-1">
                        {addon.name}
                      </h5>
                      <p className="text-gray-600 text-xs mb-1 line-clamp-1">
                        {addon.description}
                      </p>
                      
                      {/* Quantity Controls */}
                      {isSelected ? (
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border rounded-lg">
                            <button
                              onClick={() => handleUpdateQuantity(addon.id, quantity - 1)}
                              className="px-2 py-1 hover:bg-gray-100"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-2 py-1 text-xs">{quantity}</span>
                            <button
                              onClick={() => handleUpdateQuantity(addon.id, quantity + 1)}
                              className="px-2 py-1 hover:bg-gray-100"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="text-pink-600 font-bold text-sm">
                            ₹{addon.price * quantity}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-pink-600 font-bold text-sm">
                            ₹{addon.price}
                          </span>
                          <button 
                            onClick={() => handleAddAddon(addon)}
                            className="text-xs px-2 py-1 rounded bg-pink-100 text-pink-600 hover:bg-pink-200"
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Addons Summary */}
          {localAddons.length > 0 && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-green-800 text-sm">
                  Selected Add-ons ({localAddons.reduce((sum, a) => sum + a.quantity, 0)})
                </h4>
                <button
                  onClick={() => setLocalAddons([])}
                  className="text-pink-600 text-xs hover:text-pink-700"
                >
                  Clear All
                </button>
              </div>
              <div className="space-y-2">
                {localAddons.map(addon => (
                  <div key={addon.id} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 text-xs">{addon.name}</span>
                      <span className="text-gray-500 text-xs">× {addon.quantity}</span>
                    </div>
                    <span className="font-semibold text-green-700">₹{addon.price * addon.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-xs">Add-ons Total</p>
                <p className="text-gray-500 text-xs">
                  {localAddons.reduce((sum, a) => sum + a.quantity, 0)} item(s) selected
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-pink-600">
                  ₹{calculateAddonsTotal()}
                </p>
                <p className="text-gray-500 text-xs">
                  Additional to your order
                </p>
              </div>
            </div>
          </div>



          {/* Note */}
          <p className="text-center text-gray-500 text-xs mt-4">
            You can add these items later from cart too
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddonModal;